import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Loading from "../../components/Loading/Loading";
import Tabs from "../../components/Tabs/Tabs";
import FriendsGrid from "./Friends";
import MultiSectionForm from "../../components/MultiSectionForm/MultiSectionForm";
import CSS from "./Profile.module.css";
import { useUserData } from "../../hooks/UserData";
import EmptyStateCard from "./EmptyStateCard";
import { useFriendsData, useBlockedFriendsData } from "../../hooks/FriendData";
import useSaveProfileForm from "../../hooks/FormSave";
import { getSectionToFormFields } from "./utils";
import { Typography, Box } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import AccountSettings from "./AccountSettings";

const loadingSpinner = (
  <div className={CSS.loading}>
    <Loading />
  </div>
);

const Profile = ({ onLogout }) => {
  const [user, isLoading, error] = useUserData();
  const [sectionToFormFields, setSectionToFormFields] = useState({});
  const [isProfileSaving, profileSavingError, onFormsSave, updatedProfile] =
    useSaveProfileForm(false);

  const [
    friendsData,
    friendsIsLoading,
    friendsError,
    setPage,
    page,
    totalPages,
    fetchFriends,
    blockFriend,
    removeFriend,
  ] = useFriendsData();

  const [
    blockedFriends,
    blockedFriendsIsLoading,
    blockedFriendsError,
    setBlockedFriendsPage,
    blockedFriendsPage,
    blockedFriendsTotalPages,
    fetchBlockedFriends,
    unblockFriend,
  ] = useBlockedFriendsData();

  const onBlockStatusChange = async (friendId, isBlockAction) => {
    if (isBlockAction) await blockFriend(friendId);
    else await unblockFriend(friendId);
    fetchFriends();
    fetchBlockedFriends();
  };

  const onRemoveFriendChange = async (friendId) => {
    await removeFriend(friendId);
    fetchFriends();
  };

  useEffect(() => {
    if (user) setSectionToFormFields(getSectionToFormFields(user));
  }, [user]);

  useEffect(() => {
    if (updatedProfile)
      setSectionToFormFields(getSectionToFormFields(updatedProfile));
  }, [updatedProfile]);

  const TabToValue = {
    "Profile Info": (
      <Box sx={{ position: "relative" }}>
        {profileSavingError && (
          <Box className={CSS.error}>
            <Typography>An error occurred while saving.</Typography>
          </Box>
        )}
    
        <MultiSectionForm
          SectionToFormFields={sectionToFormFields}
          onFormsSave={onFormsSave}
          isSaving={isProfileSaving} // optional prop if you want to disable buttons
        />
    
        {isProfileSaving && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "16px",
              backgroundColor: "rgba(10,12,18,0.55)",
              backdropFilter: "blur(6px)",
              zIndex: 10,
            }}
          >
            <Loading />
          </Box>
        )}
      </Box>
    ),    

    Friends: friendsIsLoading ? (
      loadingSpinner
    ) : (
      <>
        <FriendsGrid
          friendsData={friendsData}
          setPage={setPage}
          page={page}
          totalPages={totalPages}
          onBlockStatusChange={onBlockStatusChange}
          onRemoveFriendChange={onRemoveFriendChange}
          type="unblocked"
        />
          {!totalPages && (
            <EmptyStateCard
              icon={<GroupIcon sx={{ fontSize: 34, color: "rgba(255,255,255,0.90)" }} />}
              title="No friends yet"
              subtitle="Start matching and like your matches 👀"
            />
          )}
      </>
    ),

    Blocked: blockedFriendsIsLoading ? (
      loadingSpinner
    ) : (
      <>
        <FriendsGrid
          friendsData={blockedFriends}
          setPage={setBlockedFriendsPage}
          page={blockedFriendsPage}
          totalPages={blockedFriendsTotalPages}
          onBlockStatusChange={onBlockStatusChange}
          type="blocked"
        />
          {!blockedFriendsTotalPages && (
            <EmptyStateCard
              icon={<PersonIcon sx={{ fontSize: 34, color: "rgba(255,255,255,0.90)" }} />}
              title="No blocked accounts"
              subtitle="All good here — your space is clean and drama-free ✨"
            />
          )}
      </>
    ),

    Settings: <AccountSettings onLogout={onLogout} />,
  };

  return (
    <div className={CSS.profile}>
      {!error && isLoading && loadingSpinner}
      {!error && !isLoading && (
        <Tabs TabToValue={TabToValue} defaultTab="Profile Info" />
      )}
      {error && (
        <Box className={CSS.error}>
          <Typography>Something went wrong.</Typography>
        </Box>
      )}
    </div>
  );
};

Profile.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

export default Profile;
